/**
 * ChatContext — Thin provider that exposes chat orchestration to the app.
 *
 * All logic lives in:
 *   • stores/chatStore.js          (Zustand state)
 *   • hooks/useChatLogic.js        (orchestration & message flow)
 *   • hooks/useStreaming.js        (network APIs)
 *   • hooks/useDrafting.js         (draft job polling)
 *   • lib/chatHelpers.js           (pure helpers)
 *   • lib/chatConstants.js         (constants)
 *
 * The exported `useChat()` hook keeps its original API contract so all
 * downstream components (ChatPage, Sidebar, ArtifactPanel, etc.) keep
 * working without modification.
 */
import React, { createContext, useContext } from 'react';
import { useChatLogic } from '@/hooks/useChatLogic';

const ChatContext = createContext(undefined);

export const ChatProvider = ({ children }) => {
  const value = useChatLogic();
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
